<script lang="ts" generics="T extends { id: number, label: string }">
  import { clickOutside } from "$app/attachments.svelte"
  import { onMount, type Snippet } from "svelte"

  type Props<T> = {
    onselect: (data: T[]) => void
    selected: T[]
    options: T[]
    contName?: string
    open?: boolean
    searchField?: string
    valueField?: string
    placeholder?: string
    borderClass?: string
    selectedSnippet?: Snippet<[T, number]>
    optionSnippet?: Snippet<[T, number]>
  }

  let props = $props()

  let {
    onselect,
    options,
    contName,
    searchField,
    valueField,
    placeholder,
    borderClass = "",
    selectedSnippet,
    optionSnippet
  }: Props<T> = props
  let selected = $state(props.selected || []) as T[]
  let open = $state(props.open || true)

  let filteredOptions: T[] = $state([])
  let inputVal = $state("")
  let inputElem = $state<HTMLInputElement | null>(null)
  let index = $state<number | null>(null)

  let firstFocus = $state(false)

  function onFocus(): void {
    open = true
    firstFocus = true
    setTimeout(() => {
      inputElem.focus()
    }, 10)
  }

  function onKey(evt: KeyboardEvent): void {
    const prevVal = inputVal
    firstFocus = false
    switch (evt.key) {
      case "Enter":
        selectIndex()
        break
      case "Backspace":
        setTimeout(() => {
          if (prevVal === inputVal) {
            removeOption(selected[selected.length - 1])
          }
          filterOptions()
        }, 10)
        break
      case "ArrowDown":
        moveDown()
        break
      case "ArrowUp":
        moveUp()
        break
      default:
        filterOptions()
        break
    }
  }

  function filterOptions(): void {
    if (inputVal.trim() !== "") {
      const reg = new RegExp(inputVal.trim(), "ig")

      filteredOptions = options.filter((opt) => {
        return reg.test(opt[searchField])
      })
    } else {
      filteredOptions = [...options]
    }
  }

  export function resetIndex() {
    index = null
  }

  export function moveDown(): void {
    if (index === null) {
      index = 0
      return
    }
    if (index + 1 === options.length) {
      index = 0
    } else {
      index++
    }
  }

  export function moveUp() {
    if (index === null) {
      index = options.length - 1
      return
    }
    if (index === 0) {
      index = options.length - 1
    } else {
      index--
    }
  }

  export function selectIndex() {
    if (index !== null) {
      addOption(filteredOptions[index])
    }
  }

  function removeOption(item: any) {
    const idx = selected.findIndex(
      (flt) => flt[valueField] === item[valueField]
    )
    inputVal = ""

    if (idx > -1) {
      const sel = selected[idx]
      selected = selected.filter((s) => s.id !== sel.id)
      setFilteredOptions()
      onselect(selected)
    }
    inputElem.focus()
  }

  function setFilteredOptions(): void {
    filteredOptions = [...options].filter(
      (opt) => !selected.map((s) => s.id).includes(opt.id)
    )
  }

  function addOption(option: T) {
    selected = [...selected, option]

    setFilteredOptions()
    inputVal = ""
    onselect(selected)
    index = null
  }

  onMount(() => {
    setFilteredOptions()
  })
</script>

<div class="multi-select-cont relative">
  <div
    class={[
      "select-cont border rounded pb-2",
      borderClass ? borderClass : "border-gray-200"
    ]}
    data-name={contName}
    {@attach clickOutside(() => (open = false))}
  >
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div
      onclick={onFocus}
      role="button"
      tabindex="0"
      aria-label="Select"
      class="gap-4"
    >
      {#each selected as option, idx (idx)}
        <span class="mt-2 bg-gray-50 border border-gray-100 rounded p-1 mx-1">
          {#if selectedSnippet}
            {@render selectedSnippet(option, idx)}
          {:else}
            <span class="">
              {option[searchField]}
            </span>
          {/if}
          <button
            class="remove-selected cursor-pointer text-gray-400 border-gray-400 font-bold text-xl"
            onclick={() => removeOption(option)}
            type="button"
          >
            &times;
          </button>
        </span>
      {/each}
      <input
        type="text"
        class="border rounded focus:ring-transparent px-2 mt-2 py-2 text-blue-900 shadow"
        {placeholder}
        style="order:1000000000"
        bind:this={inputElem}
        bind:value={inputVal}
        onkeydown={onKey}
      />
    </div>
  </div>

  <div
    class="absolute options-cont bg-white w-full shadow-md z-10"
    class:hidden={!open}
  >
    <ul>
      {#each filteredOptions as option, idx (idx)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <li
          onmouseenter={() => {
            index = idx
          }}
          class:bg-blue-100={idx === index}
          onclick={() => addOption(option)}
        >
          {#if optionSnippet}
            {@render optionSnippet(option, idx)}
          {:else}
            <div class="p-2">
              {option[searchField]}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  :root {
    --multi-select-bg-color: #ffffff;
    --multi-select-padding-x: 1rem;
    --multi-select-max-height: 20em;
  }

  .select-cont {
    overflow: hidden;
    background-color: var(--multi-select-bg-color);
  }

  .select-cont > div {
    padding-left: var(--multi-select-padding-x);
    padding-right: var(--multi-select-padding-x);
  }

  .select-cont input[type="text"] {
    padding-left: 0.5rem;
  }

  .select-cont > div > span {
    display: inline-block;
  }

  .options-cont {
    overflow-y: auto;
    max-height: 20em;
  }

  .remove-selected {
    cursor: pointer;
    width: 1.2rem;
    height: 1.2rem;
    font-weight: bold;
    font-size: 1rem;
    line-height: 0.9;
    text-align: center;
    display: inline-block;
  }
</style>
